/* RESPONSIBLE TEAM: team-conversational-knowledge */

import type AppService from 'embercom/services/app-service';
import Service from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { TrackedSet } from 'tracked-built-ins';
import { type TopicItem } from 'embercom/components/reporting/automation/ai-insights/topics-side-panel';
import AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import { EntityType } from 'embercom/models/data/entity-types';
import { timeout } from 'ember-concurrency';
import ENV from 'embercom/config/environment';
import RouteRegexes from 'embercom/lib/route-regexes';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import { restartableTask } from 'ember-concurrency-decorators';
import { getOwner } from '@ember/application';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import type { ReviewType } from 'embercom/models/content-service/content-review-request';
import { get } from 'embercom/lib/ajax';
import { SortableFields } from 'embercom/lib/suggestions/constants';
import type ReportingChartService from 'embercom/services/reporting-chart-service';

export type TopicSummary = {
  topic: string;
  total_count: number;
  subtopics: {
    subtopic: string;
    count: number;
    review_type_counts: ReviewTypeCount[];
  }[];
  review_type_counts: ReviewTypeCount[];
};

type TopicDetails = {
  totalCount: number;
  subtopics: {
    name: string;
    count: number;
    reviewTypeCounts: ReviewTypeCount[];
  }[];
  reviewTypeCounts: ReviewTypeCount[];
};

export type ReviewTypeCount = {
  review_type: ReviewType;
  count: number;
};

export type EntityIdentifierTuple = { entityId: number; entityType: EntityType };

export default class ContentSuggestionsService extends Service {
  @service declare router: RouterService;
  @service declare reportingChartService: ReportingChartService;
  @service declare appService: AppService;

  @tracked declare listApi: AiContentLibraryApi;
  @tracked selectedTopicItems?: Array<ContentWrapper>;
  @tracked topics: Array<TopicItem> = [];
  @tracked suggestionsForTopic: ContentWrapper[] = [];
  @tracked topicsSummary: Map<string, TopicDetails> = new Map();
  completedSuggestions: TrackedSet<string> = new TrackedSet();

  get isOnSuggestionsPage() {
    return RouteRegexes.automation.contentSuggestions.test(this.router.currentRouteName);
  }

  async refreshSuggestionsListWithDelay() {
    // On the AI insights page we are optimistically removing the item from the list, and then immediately refreshing it without a delay.
    // On the content suggestions page we are not removing the item from the list, giving ES time to update and then refreshing it.
    if (!this.isOnSuggestionsPage) {
      return;
    }

    if (!this.listApi) {
      return;
    }

    // Let's give ES some time to update
    await timeout(ENV.APP._500MS);
    this.listApi.resetFiltersForSearch();
    await this.listApi.loadPage();
  }

  @restartableTask
  *loadSuggestionsForTopic(topic: string, subtopic?: string | null) {
    let additionalSearchableData: { topic: string; subtopics?: string[] } = {
      topic,
    };
    if (subtopic) {
      additionalSearchableData.subtopics = [subtopic];
    }

    if (this.listApi) {
      yield this.listApi.searchByTopic(additionalSearchableData);
    } else {
      let api = new AiContentLibraryApi(getOwner(this), {
        object_types: [EntityType.ArticleContent, EntityType.ContentSnippet],
        content_wrapper_types: [ContentWrapperType.AI_CONTENT],
        app_id: this.appService.app.id as string,
        per_page: 100,
        content_review_statuses: [Status.Pending],
        additional_searchable_data: additionalSearchableData,
        sort_by: SortableFields.Priority,
        sort_direction: 'desc',
      });

      yield api.loadPage();
      this.listApi = api;
    }

    this.suggestionsForTopic = this.listApi.contentWrappers;
  }

  @restartableTask
  *loadQuestionAnswerPairsForGettingStarted(): Generator<any, any, any> {
    let response = yield get('/ember/ai_insights/example_question_answers', {
      app_id: this.appService.app.id,
      limit: 6,
      ...(this.reportingChartService.useSyntheticData
        ? { synthetic_data_use_case: this.reportingChartService.syntheticDataUseCase }
        : {}),
    });
    return response;
  }

  getSuggestionCountForTopics(topics: string[]): number {
    if (isEmpty(topics)) {
      return 0;
    }

    let sum = 0;
    for (let topic of topics) {
      let topicDetails = this.topicsSummary.get(topic);
      if (topicDetails) {
        sum += topicDetails.totalCount;
      }
    }
    return sum;
  }

  getTopicsSuggestionCount(topicId: string, subTopicId: string | null): number {
    if (subTopicId) {
      // If subTopicId is set, then we need to get the count for the subtopics grouped by the topicId
      return (
        this.topicsSummary.get(topicId)?.subtopics.find((subtopic) => subtopic.name === subTopicId)
          ?.count ?? 0
      );
    }
    return this.topicsSummary.get(topicId)?.totalCount ?? 0;
  }

  async fetchTopicsSummary(useTimeout = false): Promise<void> {
    if (useTimeout) {
      await timeout(ENV.APP._1000MS);
    }
    let topicsSummary: TopicSummary[] = await get('/ember/ai_insights/topics_summary', {
      app_id: this.appService.app.id,
    });

    // convert to map for faster lookup
    this.topicsSummary = topicsSummary.reduce(
      (
        map: Map<string, TopicDetails>,
        { topic, total_count, subtopics, review_type_counts }: TopicSummary,
      ) => {
        map.set(topic, {
          totalCount: total_count,
          subtopics: subtopics.map(({ subtopic, count, review_type_counts }) => ({
            name: subtopic,
            count,
            reviewTypeCounts: review_type_counts,
          })),
          reviewTypeCounts: review_type_counts,
        });
        return map;
      },
      new Map(),
    );
  }

  getNextSuggestionFromList(content: EntityIdentifierTuple): EntityIdentifierTuple | undefined {
    if (!this.listApi || this.listApi.contentWrappers.length === 0) {
      return;
    }

    let currentIndex = this.listApi.contentWrappers.findIndex(
      (wrapper) =>
        wrapper.entityId === content.entityId && wrapper.entityType === content.entityType,
    );
    if (currentIndex === -1) {
      // If the current content is not found in the list, then we need to return the first content in the list if available
      let firstContent = this.listApi.contentWrappers?.[0];
      return firstContent
        ? {
            entityId: firstContent.entityId!,
            entityType: firstContent.entityType,
          }
        : undefined;
    }

    let nextIndex;
    if (currentIndex === this.listApi.contentWrappers.length - 1 && currentIndex !== 0) {
      nextIndex = currentIndex - 1;
    } else {
      nextIndex = currentIndex + 1;
    }

    let nextContent = this.listApi.contentWrappers[nextIndex];
    if (!nextContent) {
      return;
    }

    return {
      entityId: nextContent.entityId!,
      entityType: nextContent.entityType,
    };
  }

  markSuggestionAsCompleted(content: EntityIdentifierTuple) {
    let key = `${content.entityId}-${content.entityType}`;
    this.completedSuggestions.add(key);
  }

  isSuggestionCompleted(content: EntityIdentifierTuple): boolean {
    if (!content || isEmpty(content.entityId) || isEmpty(content.entityType)) {
      return false;
    }
    let key = `${content.entityId}-${content.entityType}`;
    return this.completedSuggestions.has(key);
  }

  removeSuggestionFromList(content: { entity_id: number; entity_type: EntityType }) {
    let key = `${content.entity_id}-${content.entity_type}`;
    this.completedSuggestions.delete(key);
    this.removeFromContentWrapperList(content);
    this.removeFromSelectedTopicItems(content);
    this.updateTopicList();
  }

  updateTopicList() {
    this.topics = [];
    this.listApi?.contentWrappers.forEach((wrapper) => {
      let topic = wrapper.contentReviewSuggestionMetadata.topic;
      let existing = this.topics.find((t) => t.topic === topic);

      if (existing) {
        existing.count++;
        existing.items = [...existing.items, wrapper];
        wrapper.contentReviewSuggestionMetadata.included_conversations?.forEach(
          (conversationId: number) => {
            existing?.conversationIds.add(conversationId);
          },
        );
      } else {
        let conversationIds = new Set<number>();
        wrapper.contentReviewSuggestionMetadata.included_conversations?.forEach(
          (conversationId: number) => {
            conversationIds.add(conversationId);
          },
        );
        this.topics.push({ topic, count: 1, items: [wrapper], conversationIds });
      }
    });
    this.topics = this.topics
      .filter((topic) => !isEmpty(topic.topic))
      .sort((a, b) => b.conversationIds.size - a.conversationIds.size);
  }

  private removeFromContentWrapperList(content: { entity_id: number; entity_type: EntityType }) {
    if (this.listApi) {
      let index = this.listApi.contentWrappers.findIndex(
        (wrapper) =>
          wrapper.entityId === content.entity_id && wrapper.entityType === content.entity_type,
      );
      if (index !== -1) {
        this.listApi.contentWrappers = [
          ...this.listApi.contentWrappers.slice(0, index),
          ...this.listApi.contentWrappers.slice(index + 1),
        ];
        this.listApi.totalCount -= 1;
      }
    }
  }

  private removeFromSelectedTopicItems(content: { entity_id: number; entity_type: EntityType }) {
    if (this.selectedTopicItems) {
      let index = this.selectedTopicItems.findIndex(
        (wrapper) =>
          wrapper.entityId === content.entity_id && wrapper.entityType === content.entity_type,
      );
      if (index !== -1) {
        this.selectedTopicItems = [
          ...this.selectedTopicItems.slice(0, index),
          ...this.selectedTopicItems.slice(index + 1),
        ];
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    contentSuggestionsService: ContentSuggestionsService;
    'content-suggestions-service': ContentSuggestionsService;
  }
}
